import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useMemo,
  useImperativeHandle,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";

import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import TabPanel from "@mui/lab/TabPanel";
import "./SnGrid.css"; // 커스텀 CSS 추가

import { menuInfo } from "@shared/type/CommonType";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  ModuleRegistry,
  ExcelExportModule,
  RowGroupingModule,
  ClientSideRowModelModule,
  GridChartsModule,
  ColDef,
  EditableCallbackParams, // 통합 차트 모듈
  GridApi,
  GridOptions,
  ColumnState,
  CellKeyDownEvent,
  Column,
  NavigateToNextCellParams,
  ColGroupDef,
  RowNode,
  CellFocusedEvent,
  GetMainMenuItemsParams,
  MenuItemDef,
  RowSelectionOptions,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
  IRowNode,
  DefaultMenuItem,
} from "ag-grid-enterprise";
import { v4 as uuidv4 } from "uuid";
import { global, useMenuStore } from "@shared/libs/Global";
import { getDate } from "date-fns";
import { showDialog } from "src/widgets/CommonDialog";
import SnTab from "@shared/components/SnTab";
import { isDateValue, isNumeric } from "@shared/utils/CommonUtils";
import { StyleSettings } from "../../../styles/StyleSettings";

import SnTabHeader from "../SnTabHeader";
import SnFilterSearchBox from "../SnFilterSearchBox";

import CustomHeaderCheckbox from "./CustomHeaderCheckbox";
import DesignRemovableHeader from "./DesignRemovableHeader";
import {
  RowData,
  MyColDef,
  SnGridProps,
  ColumnConfig,
  CustomColDef,
  FlatRowType,
  ChangeColumnConfig,
  ChangeGroupColumnConfig,
} from "./SnGrid_Types";
import { createColumnDefs } from "./SnGrid_ColumnDefs";
import { SnGridDefaultOptions, SnGridLocaleText } from "./SnGrid_Constants";

import { createGridOptionsExt } from "./SnGrid_options";
import { getPopupSetting, popupHelper, deleteKeyDown } from "./SnGrid_popup";
import { createMainMenuItems } from "./SnGrid_menu";
import {
  computeFooterSummary,
  getFooterData,
  getFooterDataAsync,
} from "./SnGrid_footer";
import { showPopupModal } from "@widgets/CommonModal";
import HeaderPopup from "./HeaderPopup";
import { EditValueChangedEvent } from "@shared/type/CommonType";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { Console } from "console";
import { json } from "stream/consumers";

// 필요한 모듈 등록
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ExcelExportModule,
  RowGroupingModule,
  GridChartsModule,
]);

//#region 우클릭 메뉴 한글화 작업

const localeText = SnGridLocaleText; // SnGridDefaultOptions에서 localeText 가져오기
//#region SnGrid Props

//#endregion

const SnGrid = forwardRef((props: SnGridProps, ref) => {
  // 구조 분해를 통해 props에서 필요한 속성 추출
  const {
    columnDefs, // 직접 전달된 columnDefs
    columnConfig, // columnConfig를 기반으로 columnDefs 생성
    menuInfo,
    rowData: initialRowData,
    keyField = ["KeyField"], // 기본적으로 "KeyField"를 고유 식별자로 사용
    gridOptions = {},
    style,
    onGridReady,
    enableExternalChart = false, // 독립 차트 기본값: 비활성화
    chartColumns = [], // 차트로 사용할 기본 컬럼 (없음)
    isEditable = false,
    enableAddRow = false,
    // rowSelection = "single", // 기본값: single
    rowSelection: rowSelectionMode,
    pagination = false,
    paginationPageSize = 50,
    paginationPageSizeSelector = [50, 100],
    enableRangeSelection = true,
    rowGroupPanelShow = "onlyWhenGrouping",
    animateRows = true,
    rowDragManaged = false,
    cellSelection = true,
    suppressNoRowsOverlay = true,
    suppressMenuHide = false,
    ischeckboxSelection = false, // 체크박스 셀렉션
    onRowClicked, // 로우 클릭
    onCellValueChanged, // 값 수정
    onEditValueChanged, // 값 수정
    onCellDoubleClicked, // 셀더블클릭
    onBeforeAddRow, // 행 추가 전 이벤트
    onBeforeDeleteRows, // 행 삭제 전 이벤트
    onBeforeEdit, // 수정 전 이벤트
    onFocusedRowChanged, // 새로운 prop
    onRowDoubleClicked,
    onSelectionChanged, //  추가
    title,
    onBtnAddClick,
    onBtnDeleteClick,
    header,
    onContextMenu,
    resizeController,
    popupParent,
    onDesignColumnRemove, // 디자인 모드: 헤더 X로 컬럼 제거 (메뉴디자이너 미리보기)
  } = props;
  //#region KeyField 고유 키 생성 함수
  const usedKeys = new Set<string>(); // 이미 사용된 키를 추적
  const [errorMap, setErrorMap] = useState(new Map()); // 에러메시지
  const { menuItems } = useMenuStore();

  // combo 캐시
  const comboCacheRef = useRef<
    Record<string, Array<{ code: string; name: string }>>
  >({});

  // const navigate = useNavigate();
  // 전역 변수 선언

  let cdGrid: string | null = null;

  const generateKey = () => {
    let key;
    do {
      key = uuidv4(); // 항상 새로운 UUID 생성
    } while (usedKeys.has(key)); // 중복 여부 확인
    usedKeys.add(key);
    return key;
  };

  //#endregion
  const { userInfo } = global();
  // State로 columnDefs 관리
  const [resolvedColumnDefs, setResolvedColumnDefs] = useState<ColDef[]>([]);
  const firstEditableColList = ["no_style", "no_style1"]; // addRow에서 수정 가능한 첫번째 컬럼 찾을 때 no_로 시작하지만 focus 되어야하는 목록

  // editable 여부를 제어하는 함수
  const isEditableFunc = (field: string): boolean => {
    return isEditable && field !== "readonly_field";
  };

  // 이전 포커스된 행 데이터 저장용 Ref
  const previousFocusedRowRef = useRef<RowData | null>(null);
  const isProcessingRef = useRef<boolean>(false); // 엔터 두번으로 팝업이 두번 오픈됨을 막는
  // columnConfig 변경 시에만 createColumnDefs 실행
  const memoizedColumnConfig = useMemo(() => columnConfig, [columnConfig]);
  const [requireds, setRequireds] = useState<any>({}); /* */
  const [multipopups, setMultipopup] = useState<any>({}); /* */
  const [singlepopups, setSinglepopup] = useState<any>({}); /* */
  const [popupCons, setPopupCons] = useState<any>({}); /* */
  const [recoveryData, setRecoveryData] = useState<any>({}); /* */
  const [editCons, setEditCons] = useState<any>({});
  useEffect(() => {
    const loadColumnDefs = async () => {
      let updatedColumnDefs:
        | any[]
        | ((prevState: ColDef<any, any>[]) => ColDef<any, any>[]) = [];

      if (columnDefs) {
        // columnDefs가 직접 전달된 경우 그대로 설정
        updatedColumnDefs = columnDefs;
      } else if (memoizedColumnConfig) {
        // columnConfig가 변경된 경우에만 비동기 함수 호출

        // memoizedColumnConfig의 첫 번째 항목에서 cd_grid 추출
        cdGrid = memoizedColumnConfig[0]?.cd_grid || null;

        // 2025.01.08 필수값 체크용
        const requiredsFilter = memoizedColumnConfig.filter(
          (col) => col.yn_required == "Y"
        );
        let tempreq = Object.assign({});
        requiredsFilter.map((req) => {
          tempreq[req.cd_column] = req.nm_column;
        });
        setRequireds(tempreq);

        const editConsFilter = memoizedColumnConfig.filter(
          (col) => col.dc_editcon != "" && col.dc_editcon != null
        );
        let tempeditCons = Object.assign({});
        editConsFilter.map((con) => {
          tempeditCons[con.cd_column] = con.dc_editcon;
        });
        setEditCons(tempeditCons);

        // 2025.01.14 팝업용
        const multiSetting = memoizedColumnConfig.filter(
          (col) => col.tp_column == "SnMultiCodeBox"
        );
        setMultipopup(multiSetting);
        const singleSetting = memoizedColumnConfig.filter(
          (col) => col.tp_column == "SnCodeBox"
        );
        setSinglepopup(singleSetting);

        const generatedColumnDefs = await createColumnDefs(
          memoizedColumnConfig,
          isEditableFunc,
          userInfo,
          errorMap
          // ,          gridRefcol
        );
        // updatedColumnDefs = generatedColumnDefs;
        updatedColumnDefs = generatedColumnDefs.map((colDef) => ({
          ...colDef,
          mainMenuItems: mainMenuItems, // 공통 커스텀 메뉴 추가
        }));
      }
      // 디자인 모드: 각 컬럼 헤더에 제거(X) 버튼 부착 (기존 커스텀 헤더는 유지, 그룹은 children까지)
      if (onDesignColumnRemove) {
        const attachRemovableHeader = (colDef: any): any => {
          if (colDef.children) {
            return {
              ...colDef,
              children: colDef.children.map(attachRemovableHeader),
            };
          }
          if (colDef.headerComponent) return colDef;
          return {
            ...colDef,
            headerComponent: DesignRemovableHeader,
            headerComponentParams: {
              ...(colDef.headerComponentParams || {}),
              onRemove: onDesignColumnRemove,
            },
          };
        };
        updatedColumnDefs = updatedColumnDefs.map(attachRemovableHeader);
      }

      // checkboxSelection이 필요한 경우 컬럼 추가

      if (ischeckboxSelection) {
        updatedColumnDefs = [
          {
            headerName: "",
            cellRendererSelector: (params: any) => {
              if (params.node?.rowPinned) {
                return { component: "" }; // footer는 아무것도 안 보여줌
              }
              return { component: "agCheckboxCellRenderer" };
            },
            cellEditor: "agCheckboxCellEditor",
            width: 20,
            colId: "fixCheckbox",
            field: "fixCheckbox",
            lockPosition: "left", // 다른 컬럼이 이 앞으로 이동 불가
            lockPinned: true, // 헤더 메뉴로 고정 해제 불가
            suppressMovable: true, // 이동 불가
            headerComponent: CustomHeaderCheckbox,
            editable: true,
            cellStyle: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
          },
          ...updatedColumnDefs,
        ];
      }
      setResolvedColumnDefs(updatedColumnDefs);
    };
    loadColumnDefs();
  }, [columnDefs, memoizedColumnConfig]);

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter", // 헤더 필터 비활성화 (타입을 명시해야 정상 작동됨.)
      floatingFilter: false, // 플로팅 필터 비활성화
      resizable: true,
      suppressMenu: true,
      enableRowGroup: true,
      editable: isEditable, // 수정 가능 여부를 props로 제어
      headerClass: "header-center",
    }),
    []
  );

  const columnSave = async () => {
    try {
      if (!memoizedColumnConfig || memoizedColumnConfig.length === 0) return;
      const columnState = gridRef.current?.api
        .getColumnState()
        .filter((colDef: any) => !colDef.colId?.startsWith("fix"));
      if (!columnState || columnState.length === 0) return;

      const currentColumnDefs = gridRef.current?.api
        .getColumnDefs()
        ?.filter(
          (colDef: any) => !colDef.colId?.startsWith("fix")
        ) as CustomColDef[];
      if (!currentColumnDefs || currentColumnDefs.length === 0) return;
      const updatedColumnDefs = memoizedColumnConfig.map((colDef: any) => {
        // columnState에서 동일한 colId 찾기
        const matchingColumn = columnState?.find(
          (state) => state.colId === colDef.cd_column
        );
        const matchingIndex = columnState?.findIndex(
          (state) => state.colId === colDef.cd_column
        );

        const matchingColumnDefs = currentColumnDefs?.find(
          (state) => state.colId === colDef.cd_column
        );

        return {
          ...colDef,
          cd_user: userInfo.cd_user,
          nm_column: matchingColumnDefs?.headerName ?? colDef.nm_column,
          qt_width: matchingColumn?.width ?? colDef.qt_width,
          no_seq: matchingIndex !== -1 ? matchingIndex : colDef.no_seq,
          fg_visible:
            matchingColumn?.hide !== undefined
              ? matchingColumn.hide
                ? "N"
                : "Y"
              : colDef.fg_visible,
        };
      });

      if (memoizedColumnConfig[0].cd_user !== userInfo.cd_user) {
        modifiedRows.current.added.push(...updatedColumnDefs);
      } else {
        modifiedRows.current.updated.push(...updatedColumnDefs);
      }
      const changesRow = getChange();
      const SaveData = {
        sys_formgridcolumn: changesRow,
      };
      const response = await fnSave("grid", SaveData);
      resetUpdate();
    } catch (error: any) {}
  };
  const headerChange = async () => {
    if (!gridRef.current) return;

    const currentColumnDefs =
      gridRef.current?.api.getColumnDefs() as CustomColDef[];
    const results: any[] | string = await showPopupModal({
      title: "항목명변경",
      children: <HeaderPopup dataSource={currentColumnDefs as any[]} />,
      width: "300px",
      height: "550px",
    });
    if (!results || results.length === 0 || results === "close") return;
    const updatedColumnDefs: CustomColDef[] = (currentColumnDefs ?? [])
      .filter((colDef) => colDef.headerName !== "No.")
      .map((colDef) => {
        const result = Array.isArray(results)
          ? results.find((r) => r.field === colDef.colId)
          : undefined;

        if (result && result.headerName !== undefined) {
          return {
            ...colDef,
            headerName: result.headerName,
          } as CustomColDef;
        }

        return colDef;
      });

    setResolvedColumnDefs(updatedColumnDefs);
  };

  const columnInit = async () => {
    try {
      if (!memoizedColumnConfig || memoizedColumnConfig.length === 0) return;

      if (memoizedColumnConfig[0].cd_user === userInfo.cd_user) {
        modifiedRows.current.deleted.push(...memoizedColumnConfig);
      }
      const changesRow = getChange();
      const SaveData = {
        sys_formgridcolumn: changesRow,
      };
      const response = await fnSave("grid", SaveData);
      resetUpdate();
    } catch (error: any) {}
  };

  const filterBoxRef = useRef<{ openFilterBox: () => void } | null>(null);

  // 높이 변환 함수
  function parseSizeToPx(value: any, containerSize: number): number {
    if (typeof value === "number") return value;
    if (!value) return 0;

    if (value.endsWith("px")) {
      return parseInt(value, 10);
    } else if (value.endsWith("%")) {
      return Math.round((containerSize * parseFloat(value)) / 100);
    } else {
      return parseInt(value, 10);
    }
  }

  const mainMenuItems = (params: GetMainMenuItemsParams) => {
    return createMainMenuItems(params, {
      seq,
      resizeController,
      columnSave,
      columnInit,
      headerChange,
      filterBoxRef,
      enableRowDrag,
      props,
    });
  };

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 200,
      suppressMenu: false,
      cellRendererParams: {
        innerRenderer: (params: any) => {
          return params.value;
        },
      },
      mainMenuItems: (
        params: GetMainMenuItemsParams
      ): (MenuItemDef | DefaultMenuItem)[] => {
        const customMenuItems: MenuItemDef[] = [
          {
            name: "모든 그룹 해제",
            action: () => {
              params.api.setRowGroupColumns([]); // 모든 그룹 해제
            },
          },
          {
            name: "전체 확장",
            action: () => {
              params.api.expandAll(); // 전체 확장
            },
          },
          {
            name: "전체 축소",
            action: () => {
              params.api.collapseAll(); // 전체 축소
            },
          },
        ];

        // ✅ 필요하다면 기본 메뉴 항목도 추가 가능
        // return ['pinSubMenu', 'separator', ...customMenuItems];
        return customMenuItems;
      },
    };
  }, []);

  const enableRowDrag = () => {
    if (!gridRef.current) return;

    let currentColumnDefs = gridRef.current?.api.getColumnDefs();

    if (currentColumnDefs) {
      const currentFilterModel = gridRef.current.api.getFilterModel();

      currentColumnDefs = currentColumnDefs.filter(
        (col: any) => col.colId !== "fixIndicator"
      );
      const isRowDragColumnExists = currentColumnDefs.some(
        (col: any) => col.colId === "fixDrag"
      );

      if (isRowDragColumnExists) {
        currentColumnDefs = currentColumnDefs.filter(
          (col: any) => col.colId !== "fixDrag"
        );
        setResolvedColumnDefs(currentColumnDefs); // 상태 업데이트
      } else {
        const updatedColumnDefs = [
          {
            headerName: "순서 변경",
            field: "rowDrag",
            rowDrag: true,
            editable: false,
            width: 50,
            colId: "fixDrag",
            sortable: false,
            filter: false,
            lockPosition: "left" as const, // 다른 컬럼이 이 앞으로 이동 불가
            lockPinned: true, // 헤더 메뉴로 고정 해제 불가
            suppressMovable: true,
          },
          ...currentColumnDefs,
        ];

        setResolvedColumnDefs(updatedColumnDefs); // 상태 업데이트
      }
    }
  };
  //#endregion

  //#region 초기화

  const gridRef = useRef<AgGridReact>(null); // AG Grid 내부 참조

  const [isGridReady, setIsGridReady] = useState(false);

  // Generate a unique storage key based on URL and component ID
  const getStorageKey = () => {
    // debugger;
    const baseKey = menuInfo?.cd_menu; // Current page path

    const componentId = cdGrid || "defaultGrid"; // gridId가 없으면 기본값 사용
    return `gridColumnState_${baseKey}_${componentId}`;
  };

  // Save column state to local storage
  const saveColumnState = () => {
    //debugger;
    if (gridRef.current?.api) {
      const columnState = gridRef.current.api.getColumnState();
      const key = getStorageKey();

      const previousState = localStorage.getItem(key);

      if (
        previousState &&
        JSON.stringify(JSON.parse(previousState)) ===
          JSON.stringify(columnState)
      ) {
        return; // 저장하지 않음
      }

      localStorage.setItem(key, JSON.stringify(columnState));
    }
  };

  // Restore column state from local storage
  const restoreColumnState = () => {
    if (gridRef.current?.api) {
      const columnState = localStorage.getItem(getStorageKey());
      if (columnState && gridRef.current?.api) {
        const parsedState = JSON.parse(columnState);

        gridRef.current.api.applyColumnState({
          state: parsedState,
          applyOrder: true,
        });
      }
    }
  };

  // Debounce 적용
  let debounceTimeout: number;

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    return (...args: any[]) => {
      clearTimeout(debounceTimeout); // timeout을 해제
      debounceTimeout = window.setTimeout(() => func(...args), delay); // setTimeout 사용
    };
  };

  const handleColumnStateChange = debounce(() => {
    if (gridRef.current?.api) {
      // 컬럼 상태 가져오기
      const columnState = gridRef.current.api.getColumnState();

      // resolvedColumnDefs 업데이트
      const updatedColumnDefs: CustomColDef[] = columnState
        .map((state) => {
          const matchingColDef = resolvedColumnDefs.find(
            (colDef) =>
              colDef.field === state.colId || colDef.colId === state.colId
          );

          if (!matchingColDef) return null;

          return {
            ...matchingColDef,
            hide: state.hide ?? false, // hide 처리
            pinned: state.pinned ?? null, // 고정 상태
            width: state.width ?? matchingColDef.width, // 기존 너비 유지
            initialWidth: state.width ?? matchingColDef.width, // 기존 너비 유지
          } as CustomColDef; // 명시적으로 ColDef 단언
        })
        .filter((colDef): colDef is CustomColDef => colDef !== null); // null 제거 및 타입 단언

      setResolvedColumnDefs(updatedColumnDefs);
    }
  }, 300);

  // 데이터 초기화

  const [rowData, setRowData] = useState<RowData[]>(
    (initialRowData || []).map((row) => {
      const key = row.KeyField || generateKey();
      return { ...row, KeyField: key, cd_user_amd: userInfo.cd_user };
    })
  );

  useEffect(() => {
    if (isGridReady && initialRowData) {
      gridRef.current?.api.showLoadingOverlay(); // 로딩 오
      setRowData(
        initialRowData.map((row) => ({
          ...row,
          KeyField: row.KeyField || generateKey(),
          fixCheckbox: false,
        }))
      );
      if (ischeckboxSelection) {
      }
      // restoreColumnState(); // 컬럼 상태 복원
      gridRef.current?.api.hideOverlay();
    }
  }, [isGridReady, initialRowData]);

  // 변경된 행 상태 추적
  const modifiedRows = useRef({
    added: [] as RowData[],
    updated: [] as RowData[],
    deleted: [] as RowData[],
  });
  //#endregion

  //#region Footer 관련 내용
  // 하단 고정 행 데이터를 관리하는 상태
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState<any[]>([]);

  //#region Footer 합계 계산

  const formatNumber = (value: number) =>
    value ? Number(value).toLocaleString() : "0";

  // //#endregion

  // Footer 합계 계산 함수
  const handleComputeFooterSummary = () =>
    computeFooterSummary(
      gridRef.current?.api,
      resolvedColumnDefs,
      setPinnedBottomRowData,
      pinnedBottomRowData
    );

  const getFooterDataAsyncWrapper = () =>
    getFooterDataAsync(handleComputeFooterSummary, pinnedBottomRowData);

  // 초기화 및 데이터 변경 시 합계 계산
  useEffect(() => {
    handleComputeFooterSummary();
  }, [rowData]);

  //#region 1. 행 추가

  const getFirstEditableColumn = (columnDefs: ColDef[], defaultColDef: any) => {
    return columnDefs.find((col) => {
      if (col.editable === true) return true; // 명시적으로 editable이 true인 경우
      if (col.editable === false) return false; // editable이 false면 제외
      if (typeof col.editable === "function") {
        // EditableCallbackParams 타입의 더미 데이터를 제공
        const params: EditableCallbackParams = {
          node: {} as any, // 필요에 따라 노드 객체를 설정하거나 빈 객체로 처리
          data: {}, // row data
          column: {} as any, // 현재 컬럼 정보
          colDef: col, // 현재 컬럼 정의
          api: {} as any, // ag-Grid API 객체
          context: {}, // 그리드의 context 정보
        };

        // editable 함수 실행 결과 확인
        return col.editable(params);
      }
      return defaultColDef?.editable === true; // editable이 undefined면 defaultColDef를 참조
    })?.field;
  };

  const getSelectedRow = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    // return selectedRows && selectedRows.length > 0 ? selectedRows[0] : null;
    return selectedRows && selectedRows.length > 0 ? selectedRows : null;
  };

  const getSelectedRows = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    return selectedRows && selectedRows.length > 0 ? selectedRows : null;
  };

  const addRow = (newRow: RowData = {}) => {
    const currentData: RowData[] = [];
    gridRef.current?.api.forEachNode((node) => {
      if (node.data) currentData.push({ ...node.data });
    });

    if (onBeforeAddRow && onBeforeAddRow(newRow) === false) return;

    const newKey = generateKey();
    // const newData = { KeyField: newKey, ...newRow };
    const newData = {
      ...newRow,
      KeyField: newKey,
      state: "insert",
      cd_user_reg: userInfo.cd_user,
    };

    // 1. rowData 업데이트

    modifiedRows.current.added.push(newData); // 추가된 행 기록

    gridRef.current?.api.applyTransaction({ add: [newData] });

    gridRef.current?.api.clearCellSelection(); // 행추가하면서 기존 선택된 셀포커스 제거
    gridRef.current?.api.deselectAll(); // 행추가하면서 기존 선택된 로우포커스 제거

    setRowData((prev) => [...prev, newData]); // 데이터 추가

    setTimeout(() => {
      const api = gridRef.current?.api;
      if (!api) {
        console.error("Ag-Grid API is not available."); // 디버깅 로그
        return;
      }

      let newRowIndex: number | null = 0;

      gridRef.current?.api.forEachNode((node) => {
        if (node.data.KeyField === newKey) {
          newRowIndex = node.rowIndex;
        }
      });
      if (newRowIndex === null) newRowIndex = 0;

      const firstEditableCol =
        (resolvedColumnDefs as MyColDef[]).find(
          (col) =>
            col.dc_editcon !== "true" &&
            (firstEditableColList.includes(col.field || "") ||
              !col.field?.startsWith("no_"))
        )?.field || resolvedColumnDefs[0]?.field;

      if (newRowIndex !== undefined && firstEditableCol) {
        api.ensureIndexVisible(newRowIndex);
        api.setFocusedCell(newRowIndex || 0, firstEditableCol);
        api.getDisplayedRowAtIndex(newRowIndex)?.setSelected(true);
        const focusedEvent: CellFocusedEvent = {
          type: "cellFocused",
          api: api,
          column: api.getColumn(firstEditableCol)!,
          rowIndex: newRowIndex,
          context: undefined,
          rowPinned: undefined,
        };

        handleCellFocused(focusedEvent); // 기존 핸들러 호출
        requestAnimationFrame(() => {
          api.setFocusedCell(newRowIndex || 0, firstEditableCol);
        });
        const selectedSingleSetting = singlepopups.filter(
          (popup: any) => popup.cd_column === firstEditableCol
        );
        const selectedMultiSetting = multipopups.filter(
          (popup: any) => popup.cd_column === firstEditableCol
        );
        const popupSetting = getPopupSetting(
          firstEditableCol,
          singlepopups,
          multipopups
        );

        // const popupSetting =
        //   selectedSingleSetting && Object.keys(selectedSingleSetting).length > 0
        //     ? selectedSingleSetting
        //     : selectedMultiSetting;

        if (popupSetting.all.length <= 0) return;
        const focusedCell = gridRef.current?.api.getFocusedCell();
        if (isProcessingRef.current) return;
        if (focusedCell) {
          const rowNode = gridRef.current?.api.getDisplayedRowAtIndex(
            focusedCell.rowIndex
          );
          const nodeState = rowNode?.data?.["state"] === "insert";
          const nodeValue = rowNode?.data?.[firstEditableCol];
          if (nodeValue !== null && nodeValue !== undefined && nodeValue !== "")
            return;
          if (popupSetting.all.length > 0 && nodeState) {
            isProcessingRef.current = true;
            popupHelper(
              rowNode,
              selectedSingleSetting,
              selectedMultiSetting,
              popupSetting,
              firstEditableCol,
              memoizedColumnConfig,
              addRow,
              gridRef,
              popupCons
            ).finally(() => {
              isProcessingRef.current = false;
            });
          }
        }
      }
    }, 50); // 추가 후 약간의 지연
  };

  //#endregion

  //#region  2. 선택된 행 삭제

  const handleRowFocusAfterDelete = (
    deletedRowIndex: number,
    gridApi: GridApi
  ) => {
    const remainingRows = gridApi.getDisplayedRowCount();

    if (remainingRows === 0) {
      console.warn("남아 있는 행이 없습니다.");
      return;
    }

    // 삭제된 행의 이전 행으로 이동
    let rowIndexToFocus = deletedRowIndex - 1;
    if (rowIndexToFocus < 0) {
      rowIndexToFocus = 0; // 첫 번째 행으로 이동
    }

    const rowNode = gridApi.getDisplayedRowAtIndex(rowIndexToFocus);
    if (!rowNode) {
      rowIndexToFocus = deletedRowIndex; // 다음 행으로 이동
    }

    const focusedRowNode = gridApi.getDisplayedRowAtIndex(rowIndexToFocus);
    if (focusedRowNode) {
      focusedRowNode.setSelected(true); // 행 선택
      gridApi.ensureIndexVisible(rowIndexToFocus); // 행 가시화

      // 첫 번째 수정 가능한 컬럼에 포커스 설정
      const firstEditableCol = resolvedColumnDefs.find(
        (col) => col.editable
      )?.field;
      if (firstEditableCol) {
        gridApi.setFocusedCell(rowIndexToFocus, firstEditableCol);
      }
    }
  };

  const deleteSelectedRows = () => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes();

    if (!selectedNodes || selectedNodes.length === 0) {
      console.warn("삭제할 행을 선택하세요.");
      return;
    }

    const selectedKeys = selectedNodes.map((node) => node.data.KeyField);

    const rowsToDelete = rowData.filter((row) =>
      selectedKeys.includes(row.KeyField)
    );

    // 삭제된 행의 첫 번째 노드의 인덱스
    const firstSelectedIndex = selectedNodes[0]?.rowIndex || 0;
    const rowsToRemoveIndexes = selectedNodes.map((node) => node.rowIndex);
    const rowsToRemove = selectedNodes.map((node) => node.data); // 데이터만 추출
    setRecoveryData({ rows: rowsToRemove, indexes: rowsToRemoveIndexes });
    gridRef.current?.api.applyTransaction({ remove: rowsToRemove }); // 삭제 요청

    // 상태 업데이트
    // setRowData((prev) =>
    //   prev.filter((row) => !selectedKeys.includes(row.KeyField))
    // );
    const currentData: RowData[] = [];
    gridRef.current?.api.forEachNode((node) => {
      if (node.data) currentData.push({ ...node.data });
    });
    setRowData(currentData);

    // 변경된 행 관리
    rowsToDelete.forEach((row) => {
      if (
        !modifiedRows.current.added.find((r) => r.KeyField === row.KeyField)
      ) {
        modifiedRows.current.deleted.push(row);
      }
    });

    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => !selectedKeys.includes(row.KeyField)
    );

    gridRef.current?.api.clearFocusedCell();
    gridRef.current?.api.deselectAll();

    setTimeout(() => {
      if (gridRef.current?.api) {
        gridRef.current?.api.clearFocusedCell();
        gridRef.current?.api.deselectAll();
        (document.activeElement as HTMLElement)?.blur();
        handleRowFocusAfterDelete(firstSelectedIndex, gridRef.current.api);
      } else {
        console.warn("Grid API null error.");
      }
    }, 0);
  };

  const deleteRowByIndex = (rowIndex: number) => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const node = gridRef.current.api.getDisplayedRowAtIndex(rowIndex);

    if (!node) {
      console.warn(`Row at index ${rowIndex} not found.`);
      return;
    }

    const rowDataToDelete = node.data;

    gridRef.current.api.applyTransaction({ remove: [rowDataToDelete] });

    // 상태 업데이트: 삭제된 행 제거
    setRowData((prev) =>
      prev.filter((row) => row.KeyField !== rowDataToDelete.KeyField)
    );

    // 변경된 행 관리
    if (
      !modifiedRows.current.added.find(
        (addedRow) => addedRow.KeyField === rowDataToDelete.KeyField
      )
    ) {
      modifiedRows.current.deleted.push(rowDataToDelete);
    }

    //computeFooterSummary();

    // `added`에서 삭제된 행 제거
    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => row.KeyField !== rowDataToDelete.KeyField
    );

    // 삭제 후 포커스 조정
    setTimeout(() => {
      if (gridRef.current?.api) {
        handleRowFocusAfterDelete(rowIndex, gridRef.current.api);
      } else {
        console.warn("api null error.");
      }
    }, 0);
  };

  const deleteAllRows = () => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }
    console.warn("현재 렌더링된 노드 가져오기.");

    // 현재 렌더링된 노드 가져오기
    const renderedNodes = gridRef.current.api.getRenderedNodes();

    if (!renderedNodes || renderedNodes.length === 0) {
      console.warn("No visible rows to delete.");
      return;
    }

    const visibleKeys = renderedNodes.map((node) => node.data.KeyField);

    const rowsToDelete = rowData.filter((row) =>
      visibleKeys.includes(row.KeyField)
    );

    const rowsToRemove = renderedNodes.map((node) => node.data); // 데이터만 추출
    const rowsToRemoveIndexes = renderedNodes.map((node) => node.rowIndex);
    setRecoveryData({ rows: rowsToRemove, indexes: rowsToRemoveIndexes });
    gridRef.current?.api.applyTransaction({ remove: rowsToRemove }); // 삭제 요청

    // 상태 업데이트: 삭제된 행 제거
    setRowData((prev) =>
      prev.filter((row) => !visibleKeys.includes(row.KeyField))
    );

    // 변경된 행 관리
    rowsToDelete.forEach((row) => {
      if (
        !modifiedRows.current.added.find(
          (addedRow) => addedRow.KeyField === row.KeyField
        )
      ) {
        modifiedRows.current.deleted.push(row);
      }
    });

    // // Footer 재계산 추가

    //computeFooterSummary();

    // `added`에서 삭제된 행 제거
    modifiedRows.current.added = modifiedRows.current.added.filter(
      (row) => !visibleKeys.includes(row.KeyField)
    );
  };

  //#endregion

  // 포커스된 행 변경 처리
  const handleCellFocused = async (event: CellFocusedEvent) => {
    let focusedRowData: any = null;
    const focusedRowIndex = event.rowIndex; // 선택된 로우의 인덱스
    if (focusedRowIndex == null) return;
    const selectedRows = gridRef.current?.api.getSelectedRows() || []; // 선택한 로우
    const cellRanges = gridRef.current?.api.getCellRanges() || []; // 선택한 셀 수
    const focusedCell = gridRef.current?.api.getFocusedCell(); // 선택한 셀
    const focusedRowNode =
      gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex);

    let colId: string | undefined;
    if (typeof event.column !== "string") {
      colId = event.column?.getColId(); // 안전한 접근
    } else {
      colId = event.column; // 이미 string 타입인 경우 그대로 사용
    }

    if (focusedRowIndex != null) {
      focusedRowData =
        gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex)?.data ||
        null;
    }

    if (
      typeof rowSelection === "object" &&
      rowSelection !== null &&
      rowSelection.mode === "singleRow"
    ) {
      const selectedRowData = selectedRows.length > 0 ? selectedRows[0] : null;
      gridRef.current?.api.clearCellSelection(); // 기존 셀 초기화인데

      if (focusedRowData != selectedRowData) {
        gridRef.current?.api.deselectAll(); // 기존 선택 초기화

        const focusedRowNode =
          gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex);
        focusedRowNode?.setSelected(true); // 새 행 선택
      }
    }

    if (
      typeof rowSelection === "object" &&
      rowSelection !== null &&
      rowSelection.mode === "multiRow"
    ) {
      if (cellRanges.length !== 1) return;

      if (focusedCell && cellRanges.length > 0) {
        const selectedRange = cellRanges[0];
        const selectedCell = {
          rowIndex: selectedRange.startRow?.rowIndex,
          column: selectedRange.columns[0]?.getColId(),
        };
        if (
          selectedCell.rowIndex !== focusedCell.rowIndex ||
          selectedCell.column !== focusedCell.column.getColId()
        ) {
          gridRef.current?.api.clearCellSelection();
        }
      }
      const selectedRowData = selectedRows[0];
      if (focusedRowData !== selectedRowData) {
        gridRef.current?.api.deselectAll(); // 기존 선택 초기화
        const focusedRowNode =
          gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex);
        focusedRowNode?.setSelected(true); // 새 행 선택
      }
    }

    // 포커스된 행이 변경된 경우만 처리
    if (
      focusedRowData !== previousFocusedRowRef.current &&
      onFocusedRowChanged
    ) {
      previousFocusedRowRef.current = focusedRowData; // 상태 업데이트
      requestAnimationFrame(() => {
        onFocusedRowChanged(focusedRowData);
      });
    }

    if (editCons != null && editCons[colId ? colId : ""] != null) {
      let condata: string = editCons[colId ? colId : ""];

      if (
        Object.hasOwn(focusedRowData, "state") &&
        focusedRowData.state === "insert"
      )
        condata = condata.replace("new", "false");
      else condata = condata.replace("new", "true");

      Object.keys(focusedRowData).forEach((key) => {
        condata = condata.replace(key, focusedRowData[key]);
      });
      const matchingColDef = resolvedColumnDefs.find(
        (colDef) => colDef.field === colId
      );
      const colDefType = (event.column as any)?.colDef?.tp_column;
      const updatedColumnDefs = {
        ...matchingColDef,
        editable: (params: any) => {
          const safeColId = colId ?? "";
          let cond = editCons[safeColId] || "";
          const rowData = params.data;

          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });

          try {
            return !new Function(`return (${cond})`)();
          } catch (e) {
            return true;
          }
        },
      } as CustomColDef; // 명시적으로 ColDef 단언
      setResolvedColumnDefs((prevDefs) => {
        return prevDefs.map((def) =>
          def.field === colId ? { ...def, ...updatedColumnDefs } : def
        );
      });
    }
    if (isProcessingRef.current) return;

    if (event.rowIndex == null) return;
    if (colId !== undefined) {
      const selectedSingleSetting = singlepopups.filter(
        (popup: any) => popup.cd_column === colId
      );
      const selectedMultiSetting = multipopups.filter(
        (popup: any) => popup.cd_column === colId
      );

      const popupSetting = getPopupSetting(colId, singlepopups, multipopups);

      // const popupSetting =
      //   selectedSingleSetting && Object.keys(selectedSingleSetting).length > 0
      //     ? selectedSingleSetting
      //     : selectedMultiSetting;

      const skipFlag =
        popupSetting.single[0]?.cd_popup === "POPUP_PASSWORD"
          ? false
          : shouldSkipCellAction(event);
      if (skipFlag) return;

      let value = focusedRowData?.[colId];
      if (value !== null && value !== undefined && value !== "") return;
      if (popupSetting.all.length > 0) {
        isProcessingRef.current = true;
        await popupHelper(
          focusedRowNode,
          selectedSingleSetting,
          selectedMultiSetting,
          popupSetting,
          colId,
          memoizedColumnConfig,
          addRow,
          gridRef,
          popupCons
        );
      }
      isProcessingRef.current = false;
      return;
    }
  };

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    if (rowSelectionMode === "single") {
      return {
        mode: "singleRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else if (rowSelectionMode === "multiple") {
      return {
        mode: "multiRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      };
    } else {
      return "single"; // 기본값
    }
  }, [rowSelectionMode]);

  function shouldSkipCellAction(event: any): boolean {
    const gridApi = gridRef.current?.api;
    const focusedRowIndex = event.rowIndex;
    if (focusedRowIndex === null) return true;

    let colId: string | undefined;

    if (typeof event.column !== "string") {
      colId = event.column?.getColId();
    } else {
      colId = event.column;
    }

    const colDef = event.column?.getColDef?.() ?? event.colDef; // ✅ colDef 보완
    const node = event.node ?? gridApi?.getDisplayedRowAtIndex(focusedRowIndex);

    let focusedRowData: any =
      gridRef.current?.api.getDisplayedRowAtIndex(focusedRowIndex)?.data ??
      null;

    if (!focusedRowData) return true;
    return typeof colDef.editable === "function"
      ? !colDef.editable({
          data: focusedRowData,
          colDef: colDef,
          column: event.column,
          node: node,
          api: event.api,
          context: event.context,
        })
      : colDef.editable !== true;
  }

  const keyMapRef = useRef<Record<string, string>>({});
  // 키보드 입력 처리
  const handleCellKeyDown = async (event: CellKeyDownEvent) => {
    const gridEvent = event.event;

    if (!gridEvent || !(gridEvent instanceof KeyboardEvent)) return;

    // 한글 조합(IME) 중에는 그리드가 키를 가로채지 않게 한다.
    // 조합 확정용 Enter까지 아래 stopEditing/tabToNextCell을 타면 조합이 깨져
    // 자모가 분리된 채 입력된다. SnSheet의 handleCellKeyDown1과 같은 처리다.
    if (gridEvent.isComposing || gridEvent.keyCode === 229) {
      gridEvent.stopPropagation();
      return;
    }

    const { api, node, column, colDef } = event;
    const key = gridEvent.key;
    const rowId = node.id!;
    const colId = column.getColId();
    keyMapRef.current[`${rowId}-${colId}`] = key;

    if (!node || !column || node.rowIndex === null) {
      return;
    }

    if (key === "Delete") {
      if (shouldSkipCellAction(event)) return;
      const selectedSingleSetting = singlepopupsRef.current.filter(
        (popup: any) => popup.cd_column === column.getColId()
      );
      const selectedMultiSetting = multipopupsRef.current.filter(
        (popup: any) => popup.cd_column === column.getColId()
      );
      // const popupSetting =
      //   selectedSingleSetting && Object.keys(selectedSingleSetting).length > 0
      //     ? selectedSingleSetting
      //     : selectedMultiSetting;

      const popupSetting = getPopupSetting(
        colId,
        singlepopupsRef.current,
        multipopupsRef.current
      );

      if (popupSetting.all.length > 0) {
        deleteKeyDown(
          event,
          selectedSingleSetting,
          selectedMultiSetting,
          popupSetting,
          colId,
          memoizedColumnConfig
        );
      }
      return; // Delete 눌렀으면 더 이상 다른 처리는 안 함
    }

    // 방향키 처리
    if (key === "ArrowUp" || key === "ArrowDown") {
      // 방향키로 컬럼헤더로 이동 못하도록 막는..
      const focusedCell = gridRef.current?.api.getFocusedCell();
      if (!focusedCell || focusedCell.rowIndex === 0) {
        gridRef.current?.api.setFocusedCell(0, column.getColId());
        gridRef.current?.api.ensureIndexVisible(0);

        gridEvent.preventDefault();
        gridEvent.stopPropagation();
      }
      const isEditing = api.getEditingCells().length > 0;

      if (isEditing) {
        api.stopEditing(); // 에디트 모드 종료

        // 방향키에 따른 포커스 이동
        const targetRowIndex =
          key === "ArrowUp" ? node.rowIndex - 1 : node.rowIndex + 1;

        // 유효한 행인지 확인
        if (
          targetRowIndex >= 0 &&
          targetRowIndex < api.getDisplayedRowCount()
        ) {
          api.setFocusedCell(targetRowIndex, column.getColId()); // 동일한 컬럼에 포커스
          api.ensureIndexVisible(targetRowIndex); // 포커스된 행 가시화
        }
      }
      gridEvent.preventDefault(); // 기본 동작 차단
      gridEvent.stopPropagation(); // 이벤트 전파 차단
      return; // 방향키 처리는 여기서 종료
    }

    // 기존 방향키 로직 유지
    if (key === "ArrowLeft") {
      const columns = api.getColumns?.(); // getColumns 호출
      if (!columns || columns.length === 0) return; // null 및 빈 배열 처리

      const currentColumnIndex = columns.findIndex(
        (col) => col.getColId() === column.getColId()
      );

      if (currentColumnIndex > 0) {
        const previousColumn = columns[currentColumnIndex - 1];
        if (previousColumn?.getColId() === "indicatorColumn") {
          gridEvent.preventDefault();
        }
      }
    }

    if (
      (key === "Enter" || key === "Tab") &&
      (colDef as any).tp_column === "SnCodeBox"
    ) {
      const field = colDef.field as string | undefined;
      if (!field) return;
      const newValue = event.node?.data?.[field];
      if (!newValue) return;

      await handleCodeBoxValidation(node, colDef, newValue);
      gridEvent.preventDefault();
      gridEvent.stopPropagation();
      api.tabToNextCell();
      return;
    }

    // 2025.01.31 cellvaluechange에서 keydown으로 변경
    if (key === "Tab") {
    }
    if (key === "Enter") {
      if (isProcessingRef.current) return;
      api.stopEditing();

      let prevCell = api.getFocusedCell();

      while (true) {
        api.tabToNextCell();
        const nextCell = api.getFocusedCell();

        if (
          !nextCell ||
          (prevCell?.rowIndex === nextCell.rowIndex &&
            prevCell?.column.getColId() === nextCell.column.getColId())
        ) {
          break;
        }

        prevCell = nextCell;

        const rowData: any =
          gridRef.current?.api.getDisplayedRowAtIndex(nextCell.rowIndex)
            ?.data ?? null;

        const nextColDef = nextCell.column.getColDef();
        const nextEditable = nextCell.column.getColDef().editable;
        if (
          typeof nextEditable === "function"
            ? nextEditable({
                data: rowData,
                colDef: nextColDef,
                column: event.column,
                node: event.node,
                api: event.api,
                context: event.context,
                // 필요하다면 추가로 params 채워주기
              }) // 실행
            : true
        )
          break;
      }
      isProcessingRef.current = false;
    }
  };
  //#endregion

  const onCellClicked = async (event: any) => {
    if (!gridRef.current?.api) return;
  };

  //#region 셀 더블클릭 2025.01.14 그리드 팝업관련 추가
  const handleonCellDoubleClicked = async (params: any) => {
    const rowData = params.api.getRowNode(params.node.id);
    const column = params.colDef;
    const columnValue = params.value;
    const colId = column.field;
    const { api } = params;

    if (isProcessingRef.current) return;

    const selectedSingleSetting = singlepopupsRef.current.filter(
      (popup: any) => popup.cd_column === colId
    );
    const selectedMultiSetting = multipopupsRef.current.filter(
      (popup: any) => popup.cd_column === colId
    );

    const popupSetting = getPopupSetting(colId, singlepopups, multipopups);

    const skipFlag =
      popupSetting.single[0]?.cd_popup === "POPUP_PASSWORD"
        ? false
        : shouldSkipCellAction(params);
    if (skipFlag) return;

    // const popupSetting =
    //   selectedSingleSetting && Object.keys(selectedSingleSetting).length > 0
    //     ? selectedSingleSetting
    //     : selectedMultiSetting;

    if (popupSetting.all.length != 0) {
      isProcessingRef.current = true;
      await popupHelper(
        rowData,
        selectedSingleSetting,
        selectedMultiSetting,
        popupSetting,
        colId,
        memoizedColumnConfig,
        addRow,
        gridRef,
        popupCons
      );
    }
    isProcessingRef.current = false;

    if (column.yn_drilldown === "Y") {
      const params = {
        id: "com_CommonMapper_10",
        params: {
          cd_firm: userInfo.cd_firm,
          no_docu: columnValue,
        },
      };
      const result = await fnGetDataSet(params);
      if (result.length > 0) {
        const menuList = menuItems;
        const matchedItem = menuList.find(
          (item: any) => item.cd_menu === result[0][0].cd_menu
        );
        const cdModule = matchedItem ? matchedItem.cd_module : null;

        if (menuInfo) {
          const filePath =
            `./${cdModule}/${result[0][0].cd_menu}/${result[0][0].cd_menu}`.toUpperCase();
          const drillDown = menuList
            ? menuList.find(
                (item: any) => item?.cd_menu === result[0][0].cd_menu
              )
            : undefined;

          if (drillDown) {
            (window as any).onActivate(
              drillDown.cd_menu, // 메뉴 키
              drillDown.nm_menu, // 메뉴 제목
              drillDown.cd_module, // 모듈
              filePath, // 파일 경로
              drillDown, // 데이터
              drillDown.nm_path, // 파일경로 한글화
              rowData.data,
              "fg_drilldown.Search"
            );
          }
        }
      }
    }
  };

  const singlepopupsRef = useRef(singlepopups);
  const multipopupsRef = useRef(multipopups);

  useEffect(() => {
    singlepopupsRef.current = singlepopups;
  }, [singlepopups]);
  useEffect(() => {
    multipopupsRef.current = multipopups;
  }, [multipopups]);

  //#endregion

  const selectAndFocusRow = (index: number) => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const api = gridRef.current.api;

    // 1. 기존 포커스와 선택 초기화
    api.clearFocusedCell();
    api.deselectAll(); // 기존 선택된 행 초기화

    // 행 인덱스를 확인하여 범위 내인지 체크
    const totalRowCount = api.getDisplayedRowCount();
    if (index < 0 || index >= totalRowCount) return;

    // 해당 인덱스의 행 노드를 가져오기
    const rowNode = api.getDisplayedRowAtIndex(index);
    if (rowNode) {
      // 행 선택
      rowNode.setSelected(true);

      // 해당 행이 화면에 보이도록 스크롤
      api.ensureIndexVisible(index);

      const nonCheckboxCols = resolvedColumnDefs.filter(
        (col) => !col.checkboxSelection
      );

      // 수정 가능한 컬럼 또는 기본 컬럼 선택
      const firstFocusableCol =
        (resolvedColumnDefs as MyColDef[]).find(
          (col) => col.dc_editcon !== "true"
        )?.field || resolvedColumnDefs[0]?.field;

      if (firstFocusableCol) {
        api.setFocusedCell(index, firstFocusableCol);
      }
    }
  };

  // 필더링된 행만 전체 선택하는 함수
  const selectAllFiltered = () => {
    if (!gridRef.current?.api) {
      console.warn("Grid API is not available.");
      return;
    }

    const api = gridRef.current.api;

    // 1. 기존 포커스와 선택 초기화
    api.clearFocusedCell();
    api.deselectAll(); // 기존 선택된 행 초기화

    // 2. 필터링된 rows 전체 선택
    api.selectAllFiltered();
  };

  const handleInternalonRowClicked = (event: any) => {
    if (onRowClicked) {
      onRowClicked(event); // 외부 핸들러 호출
    }
  };

  const handleInternalCellValueChanged = async (event: any) => {
    if (onBeforeEdit && onBeforeEdit(event.data, event.colDef) === false) {
      return;
    }

    const currentFocusedCell = gridRef.current?.api.getFocusedCell(); // 현재 포커스된 셀 저장

    if (onCellValueChanged) {
      onCellValueChanged(event); // 외부 핸들러 호출
    }
    if (onEditValueChanged) {
      const data = {
        rowData: event.data,
        newValue: event.newValue,
        oldValue: event.oldValue,
        rowIndex: event.rowIndex,
        colId: event.colDef.field,
        rowNode: event.node,
      };
      onEditValueChanged(data);
    }

    if (event.colDef?.field === "fixCheckbox") return;
    //// 플래시백같은 깜빡임이 나오고있음 아래 refreshCells 이걸 해줘 제외해도 된다고함. 문제 생긴다면 다른 방법을 찾아보겠음 -김찬곤-
    // const updatedRow = { ...event.data };
    // gridRef.current?.api.applyTransaction({ update: [updatedRow] });

    // 변경된 셀 강제 렌더링
    gridRef.current?.api.refreshCells({
      rowNodes: [event.node],
      //force: true, // 강제 렌더링
    });

    const index = modifiedRows.current.added.findIndex(
      (row) => row.KeyField === event.data.KeyField
    );

    if (index !== -1) {
      // KeyField가 동일한 row를 Change로 변경
      modifiedRows.current.added[index] = event.data;
    } else {
      const index = modifiedRows.current.updated.findIndex(
        (row) => row.KeyField === event.data.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRows.current.updated[index] = event.data;
      } else {
        // KeyField가 없는 경우 추가하는 로직 (선택 사항)
        modifiedRows.current.updated.push(event.data);
      }
    }

    // // 2. 합계 재계산
    handleComputeFooterSummary();
    getFooterDataAsyncWrapper();
  };

  //#endregion

  //#region getChange 변경된 데이터 가져오기
  // 2025.01.08 필수값 체크로직 추가
  const getChange = () => {
    return {
      modifiedRowcount:
        modifiedRows.current.added.length +
        modifiedRows.current.updated.length +
        modifiedRows.current.deleted.length,
      insert: modifiedRows.current.added,
      update: modifiedRows.current.updated,
      delete: modifiedRows.current.deleted,
    };
  };

  const requiredValuesCheck = () => {
    try {
      modifiedRows.current.added.map((change) => {
        Object.keys(requireds).map((required) => {
          if (
            (Object.hasOwn(change, required) &&
              (change[required] == null || change[required] == "")) ||
            !Object.hasOwn(change, required)
          ) {
            showDialog("alter", requireds[required] + "은(는) 필수값입니다");
            throw "finish";
          }
        });
      });
      modifiedRows.current.updated.map((change) => {
        Object.keys(requireds).map((required) => {
          if (
            (Object.hasOwn(change, required) &&
              (change[required] == null || change[required] == "")) ||
            !Object.hasOwn(change, required)
          ) {
            showDialog("alter", requireds[required] + "은(는) 필수값입니다");
            throw "finish";
          }
        });
      });
      return true;
    } catch (Exception) {
      if (Exception !== "finish") throw Exception;
      else return false;
    }
  };

  //#endregion

  //#region resetUpdate 저장 후 상태 초기화
  const resetUpdate = (isRecovery?: boolean) => {
    modifiedRows.current = { added: [], updated: [], deleted: [] };
    if (isRecovery) {
      if (gridRef.current?.api == null) return;
      const rows: any[] = recoveryData.rows;
      const indexes: any[] = recoveryData.indexes;
      rows.forEach((row, i) => {
        gridRef.current?.api.applyTransaction({
          add: [{ ...row }],
          addIndex: indexes[i],
        });
      });
      selectAndFocusRow(indexes[0]);
    } else {
      setRowData((prev) =>
        prev.map((row) => {
          const newRow = { ...row };
          delete newRow.block;
          delete newRow.state;
          return newRow;
        })
      );
    }
  };

  //#endregion

  //#region 하단 차트 관련 내용

  const chartContainerRef = useRef<HTMLDivElement>(null); // 독립 차트 컨테이너 참조
  const gridApiRef = useRef<any>(null); // AG Grid API
  const columnApiRef = useRef<any>(null); // AG Grid Column API

  const handleGridReady = (params: any) => {
    const { api, columnApi } = params;

    gridApiRef.current = params.api;
    columnApiRef.current = params.columnApi;

    setIsGridReady(true);

    if (onGridReady) {
      onGridReady(params); // 외부 콜백 호출
    }

    // 데이터가 렌더링된 후 기본 차트 생성
    if (
      enableExternalChart &&
      chartColumns.length > 0 &&
      chartContainerRef.current
    ) {
      try {
        api.createRangeChart({
          cellRange: {
            columns: chartColumns,
          },
          chartType: "groupedColumn", // 차트 유형
          chartContainer: chartContainerRef.current, // 독립 차트 컨테이너
        });
      } catch (error) {
        console.error("Error creating chart:", error);
      }
    }
  };

  //#endregion

  //#region 필터 관련 내용

  // 필터를 적용하는 메서드
  const applyFilters = (filterConditions: { [key: string]: any }) => {
    if (gridRef.current?.api) {
      // 1. 작업 시작: 화면 동결
      gridRef.current.api.showLoadingOverlay();
      // 2. 필터링 작업 수행
      const filterModel: { [key: string]: any } = {};

      // filterConditions에 따라 필터 모델 생성

      Object.keys(filterConditions).forEach((field) => {
        const value = filterConditions[field];

        if (value === "" || value === undefined) {
          filterModel[field] = { type: "blank" };
        } else if (Array.isArray(value)) {
          // 배열인 경우: Set Filter 방식 사용
          filterModel[field] = {
            filterType: "set",
            values: value,
          };
        } else {
          // 단일 값인 경우
          filterModel[field] = {
            type: "equals",
            filter: value,
          };
        }
      });

      gridRef.current.api.setFilterModel(filterModel);

      // 3. 필터 상태 반영
      // gridRef.current.api.onFilterChanged();

      // const updatedRowData =
      //   gridRef.current?.api.getRenderedNodes().map((node) => node.data) || [];
      // calculateFooter(updatedRowData); // 변경된 데이터를 기반으로 합계 재계산

      handleComputeFooterSummary();

      // 4. 작업 종료: 화면 활성화 (작업 완료 후 약간의 지연 추가)
      setTimeout(() => {
        gridRef.current?.api.hideOverlay();
      }, 500); // 500ms 지연 후 오버레이 제거
    }
  };

  // 필터를 초기화하는 메서드
  const resetFilters = () => {
    if (gridRef.current?.api) {
      // 1. 작업 시작: 화면 동결
      gridRef.current.api.showLoadingOverlay();

      // 2. 필터 초기화
      gridRef.current.api.setFilterModel(null);
      gridRef.current.api.onFilterChanged();

      // 3. 작업 종료: 화면 활성화
      setTimeout(() => {
        gridRef.current?.api.hideOverlay();
      }, 500); // 500ms 지연 후 오버레이 제거
    }
  };

  //#endregion

  //#region Indicator 관련 내용
  const columnDefsWithRowNumber = [
    {
      headerName: "No.", // 순번 컬럼
      valueGetter: (params: { node: { rowPinned: any; rowIndex: number } }) => {
        if (params.node.rowPinned) {
          const visibleRowCount =
            gridRef.current?.api.getDisplayedRowCount() || 0;
          return visibleRowCount;
        }
        return params.node.rowIndex + 1; // 일반 행에서는 순번 표시
      },
      colId: "fixIndicator",
      width: 70, // 너비
      pinned: "left", // 왼쪽 고정
      lockPosition: "left", // 다른 컬럼이 이 앞으로 이동 불가
      lockPinned: true, // 헤더 메뉴로 고정 해제 불가
      cellClass: "row-number-cell", // 스타일 클래스
      suppressMovable: true, // 이동 불가
      suppressNavigable: true, // 키보드 이동(탭, 방향키) 차단
      sortable: false, // 정렬 불가
      filter: false, // 필터링 불가
      mainMenuItems: mainMenuItems,
    },
    ...resolvedColumnDefs, // 기존 컬럼 정의
  ];

  //#endregion

  //#region shareData 데이터 업데이트
  const sharedCellValueChanged = (data: any) => {
    const Change = { ...data };
    // setRowData((prev) =>
    //   prev.map((row) => (row.KeyField === Change.KeyField ? Change : row))
    // );
    const rowNode = gridRef.current?.api.getRowNode(Change.KeyField);
    if (rowNode) {
      Object.assign(rowNode.data, Change);
      gridRef.current?.api.refreshCells({ rowNodes: [rowNode] });
    }

    // gridRef.current?.api.applyTransaction({ update: [Change] });
    const focusedCell = gridRef.current?.api.getFocusedCell();
    if (focusedCell) {
      const rowNode = gridRef.current?.api.getRowNode(Change.KeyField);
      if (rowNode) {
        rowNode.setSelected(true);
      }
    }

    const index = modifiedRows.current.added.findIndex(
      (row) => row.KeyField === Change.KeyField
    );

    if (index !== -1) {
      // KeyField가 동일한 row를 Change로 변경
      modifiedRows.current.added[index] = Change;
    } else {
      const index = modifiedRows.current.updated.findIndex(
        (row) => row.KeyField === Change.KeyField
      );

      if (index !== -1) {
        // KeyField가 동일한 row를 Change로 변경
        modifiedRows.current.updated[index] = Change;
      } else {
        // KeyField가 없는 경우 추가하는 로직 (선택 사항)
        modifiedRows.current.updated.push(Change);
      }
    }

    // // 2. 합계 재계산
    handleComputeFooterSummary();
  };

  const suppressEventDispatchRef = useRef(false); // 이벤트 억제 상태

  // 이벤트 억제 활성화
  const suppressEventDispatch = () => {
    suppressEventDispatchRef.current = true;
  };

  // 이벤트 억제 비활성화
  const allowEventDispatch = () => {
    suppressEventDispatchRef.current = false;
  };

  // 현재 이벤트 디스패치 가능 여부 확인
  const shouldDispatchEvent = () => !suppressEventDispatchRef.current;

  //#endregion

  //#region column 존재 여부 체크하여 setDataValue
  const setValue = (rowNode: RowNode, column: string, value: any) => {
    try {
      const gridApi = gridRef.current?.api;
      if (!gridApi || !rowNode) return;

      const colDef = gridApi.getColumnDef(column);
      if (!colDef) {
        console.warn(`Column '${column}' does not exist.`);
        return;
      }
      console.log(rowNode, rowNode.data[column]);

      const oldValue = rowNode.data[column];
      // 값이 다르거나 oldValue가 null/undefined인 경우 업데이트
      if (oldValue !== value || oldValue == null) {
        // 값 업데이트
        rowNode.setDataValue(column, value);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // 드릴다운으로 할 때 column 존재 여부 체크하여 setDataValue
  const setValueDrill = (rowNode: RowNode, column: string, value: any) => {
    const gridApi = gridRef.current?.api;

    if (!gridApi || !rowNode) return;

    const colDef = gridApi.getColumnDef(column);
    if (!colDef) {
      console.warn(`Column '${column}' does not exist.`);
      return;
    }
    const oldValue = rowNode.data[column];

    // 값이 다르거나 oldValue가 null/undefined인 경우 업데이트
    if (oldValue !== value || oldValue == null) {
      // 값 업데이트
      rowNode.setDataValue(column, value);
    }
  };
  //#endregion

  // excel export시 형변환 위해 필요한 데이터 추출 (순수 데이터)

  const getFlatDataFromGrid = (
    columnKeys: string[]
  ): Map<string, FlatRowType> => {
    const flatRowMap = new Map<string, FlatRowType>();

    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      const raw = node.data;
      const flat: FlatRowType = {};

      columnKeys.forEach((key) => {
        const rawVal = raw[key];

        let val = raw[key];
        let displayValue = rawVal;

        if (typeof val === "object" && val !== null) {
          val = val.label ?? val.text ?? val.value ?? JSON.stringify(val);
        }

        if (isDateValue(val)) {
          flat[key] = {
            value:
              val.length === 8
                ? `${val.slice(0, 4)}/${val.slice(4, 6)}/${val.slice(6, 8)}`
                : val,
            dataType: "dateTime" as const,
          };
        }
        //  앞에 0이 붙은 코드 → 먼저 처리
        else if (
          typeof val === "string" &&
          /^\d+$/.test(val) &&
          val.startsWith("0")
        ) {
          flat[key] = {
            value: `'${val}`, // 또는 '=' + '"' + val + '"'
            dataType: "string",
          };
        } else if (isNumeric(val)) {
          flat[key] = {
            value: Number(val),
            dataType: "number" as const,
          };
        } else {
          //  앞자리 0 보존을 위한 처리

          flat[key] = {
            value: val ?? "",
            dataType: "string" as const,
          };
        }
      });

      flatRowMap.set(node.id!, flat);
    });

    return flatRowMap;
  };

  const getCheckedRows = () => {
    const CheckedRows = rowData.filter((row) => row.fixCheckbox == true);
    return CheckedRows;
  };

  //#region 외부에서 접근 가능한 메서드 정의
  useImperativeHandle(ref, () => ({
    isProcessingRef: isProcessingRef,
    api: gridApiRef.current, // AG Grid API 노출
    columnApi: columnApiRef.current, // AG Grid Column API 노출
    selectAndFocusRow,
    selectAllFiltered,
    saveColumnState,
    restoreColumnState,
    getSelectedRow,
    getSelectedRows,
    onBeforeAddRow,
    addRow,
    deleteSelectedRows,
    deleteRowByIndex,
    deleteAllRows,
    getChange,
    requiredValuesCheck,
    resetUpdate,
    footer: getFooterData(pinnedBottomRowData), // pinnedBottomRowData를 인자로 전달
    getFooterDataAsync: getFooterDataAsyncWrapper,
    applyFilters,
    resetFilters,
    sharedCellValueChanged,
    suppressEventDispatch,
    allowEventDispatch,
    setValue: (rowNode: RowNode, column: string, value: any) =>
      setValue(rowNode, column, value),
    setValueDrill: (rowNode: RowNode, column: string, value: any) =>
      setValueDrill(rowNode, column, value),
    getFlatDataFromGrid,
    getCheckedRows,
    applyPopupConditions: (popupConditions: any) =>
      setPopupCons(popupConditions),
    ChangeColumnConfigs: (columnSettings: ChangeColumnConfig[]) =>
      ChangeColumnConfigs(columnSettings),
    getRowData: () => rowData,
    applyBlock,
    clearBlock,
    getMaxValue,
    stopEditing: () => gridRef.current?.api.stopEditing(),
    setComboData,
    getComboData,
    ChangeGroupColumnConfigs,
    getRowNode: (key: string) => gridApiRef.current.getRowNode(key),
    getAllGridColumns: () => gridApiRef.current.getAllGridColumns(),
    calculateTotals: getFooterDataAsyncWrapper,
    getFooterData: () => getFooterData(pinnedBottomRowData),
  }));

  // 콤보 데이터 교체
  const setComboData = (
    key: string,
    list: Array<{ code: string; name: string }>
  ): boolean => {
    const api = gridApiRef.current;
    if (!api) return false;
    //  그리드 context 캐시에 저장 (키는 colId 또는 field를 그대로 사용)
    comboCacheRef.current[key] = Array.isArray(list) ? list : [];

    //  즉시 반영
    api.refreshCells({ columns: [key], force: true, suppressFlash: true });

    return true;
  };

  const getComboData = (key: string): Array<{ code: string; name: string }> => {
    const list = comboCacheRef.current[key];
    return Array.isArray(list) ? list : [];
  };

  const getMaxValue = (column: string) => {
    if (!gridRef.current?.api) {
      return;
    }
    let maxNoLine: number = 0;
    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      const value = node.data[column];
      if (value != null) {
        if (maxNoLine === null || value > maxNoLine) {
          maxNoLine = value;
        }
      }
    });
    return maxNoLine;
  };

  //#endregion
  const applyBlock = (node: any) => {
    if (!node) return;
    setRowData((prev) =>
      prev.map((row) =>
        row.KeyField === node.data.KeyField ? { ...row, block: true } : row
      )
    );
  };

  const clearBlock = (node: any) => {
    if (!node) return;
    setRowData((prev) =>
      prev.map((row) =>
        row.KeyField === node.data.KeyField ? { ...row, block: false } : row
      )
    );
  };

  const ChangeColumnConfigs = (columnSettings: ChangeColumnConfig[]) => {
    if (!gridRef.current?.api) {
      return;
    }

    const currentColumnDefs =
      gridRef.current?.api.getColumnDefs() as CustomColDef[];

    const updatedColumnDefs: CustomColDef[] = (currentColumnDefs ?? [])
      .filter((colDef) => colDef.headerName !== "No.")
      .map((colDef) => {
        const columnSettin = columnSettings.find(
          (r) => r.cd_column === colDef.colId
        );

        if (columnSettin && columnSettin.nm_column !== undefined) {
          return {
            ...colDef,
            headerName: columnSettin.nm_column,
            hide: columnSettin.fg_visible === "Y" ? false : true,
          } as CustomColDef;
        }

        return colDef;
      });

    setResolvedColumnDefs(updatedColumnDefs);
  };

  const ChangeGroupColumnConfigs = (
    columnSettings: ChangeGroupColumnConfig[]
  ) => {
    if (!gridRef.current?.api) return;

    const currentColumnDefs =
      gridRef.current.api.getColumnDefs() as CustomColDef[];

    const updatedColumnDefs = currentColumnDefs
      // "No." 컬럼 제외
      .filter((colDef) => colDef.headerName !== "No.")
      .map((colDef) => {
        // 그룹 컬럼 처리
        if (colDef.children && colDef.children.length > 0) {
          const updatedChildren = colDef.children.map((child) => {
            if ("field" in child) {
              const childSetting = columnSettings.find(
                (r) => r.cd_column === child.field
              );
              if (childSetting) {
                return {
                  ...child,
                  headerName: childSetting.nm_column,
                  hide: childSetting.fg_visible !== "Y",
                };
              }
            }
            return child;
          });

          // groupId 제거
          const { groupId, ...colDefWithoutGroupId } = colDef;

          return {
            ...colDefWithoutGroupId,
            children: updatedChildren,
            hide: false,
          };
        }

        // 일반 컬럼 처리
        const columnSetting = columnSettings.find(
          (r) => r.cd_column === colDef.field
        );
        if (columnSetting) {
          return {
            ...colDef,
            headerName: columnSetting.nm_column,
            hide: columnSetting.fg_visible !== "Y",
          };
        }

        return colDef;
      });

    setResolvedColumnDefs(updatedColumnDefs);
  };

  //#region RowData 업데이트 후 포커스를 재설정하는 함수
  const handleRowDataUpdated = () => {
    if (gridRef.current?.api) {
      const lastSelectedNode = gridRef.current.api.getSelectedNodes()[0]; // 마지막 선택된 행 노드
      if (lastSelectedNode) {
        const rowIndex = lastSelectedNode.rowIndex ?? 0; // null 또는 undefined 시 0으로 설정
        // columnDefs가 존재할 때만 find 실행
        const firstEditableCol = columnDefs?.find(
          (col: ColDef) => col.editable
        )?.field; // 첫 번째 수정 가능한 컬럼

        if (firstEditableCol) {
          // 포커스 재설정
          gridRef.current.api.setFocusedCell(rowIndex, firstEditableCol);
        }
      }
    }
  };
  //#endregion

  //#region 메뉴마다 우클릭했을 때 popmenu가 그리드별로 다르기에 seq를 추가함. 메뉴에서 그리드에 추가해주면됨
  // sys_popmenu 테이블에 no_seq_dw임
  const { seq } = props;
  const menuItemsRef = useRef<
    Array<Array<{ name: string; action: () => void }>>
  >([]);
  // 우클릭 했을 때 커스텀을 하려했는데 비동기작업이 안된다고함.
  // 그래서 메뉴페이지 열때 값을 가져와 만들어 두고 그걸 사용하려고 추가했음.
  // 더 좋은 방법이 있다면 수정하셔도 되고 말해주셔도 되고 좋습니다.
  useEffect(() => {
    const fetchMenuItems = async () => {
      const fileName = props.menuInfo?.cd_menu;
      if (!fileName) {
        return;
      }
      const seqs = Array.isArray(props.seq)
        ? props.seq.filter((seq) => seq !== undefined)
        : [props.seq].filter((seq) => seq !== undefined);
      let allMenuItems: any[] = [];
      try {
        const results = await Promise.all(
          seqs.map(async (seq: number) => {
            const params = {
              id: "com_CommonMapper_09",
              params: {
                cd_menu: fileName,
                cd_firm: userInfo.cd_firm,
                no_seq_dw: seq,
              },
            };
            const data: any = await fnGetDataSet(params);
            if (Array.isArray(data) && data.length > 0) {
              const menuItems: { name: string; action: () => void }[] = [];
              for (let i = 0; i < data[0].length; i++) {
                const item = data[0][i];
                const menuName: string =
                  item?.nm_popmenu || item?.cd_popmenu || "";
                const cdFunction: string = item?.cd_function || "";
                menuItems.push({
                  name: menuName,
                  action: () => {
                    handleContextMenu(cdFunction);
                  },
                });
              }
              menuItemsRef.current[seq] = menuItems;
              return menuItems;
            }
            return [];
          })
        );
        allMenuItems = allMenuItems.concat(...results);
        menuItemsRef.current = allMenuItems;
      } catch (error) {
        console.error("Error fetching context menu data:", error);
      }
    };
    if (props.menuInfo?.cd_menu) {
      fetchMenuItems();
    }
  }, []);

  const gridOptionsExt = createGridOptionsExt(
    props,
    gridRef,
    menuInfo,
    menuItemsRef
  );

  const handleContextMenu = (params: any) => {
    //// 이게 필요한건지..주석처리 하고나서 확인해볼게요
    // const { rowIndex } = params; // 우클릭한 행의 인덱스
    // if (rowIndex !== undefined && rowIndex !== null) {
    //   selectAndFocusRow(rowIndex);
    // }

    if (onContextMenu) {
      onContextMenu(params); // 부모 컴포넌트에서 받은 이벤트 실행
    }
  };

  const handleOnRowDoubleClicked = (event: any) => {
    if (onRowDoubleClicked) {
      onRowDoubleClicked(event);
    }
  };

  const handleonCellDoubleClick = (event: any) => {
    if (onCellDoubleClicked) {
      onCellDoubleClicked(event);
    }
  };
  //#endregion

  //#region 드래그 후 로우 순번 재정의 하는 부분
  const handleRowDragEnd = () => {
    if (gridRef.current?.api) {
      // const updatedData = gridRef.current.api.getDisplayedRowAtIndex(0)!.data;
      const allData: RowData[] = [];

      gridRef.current.api.forEachNode((node, index) => {
        if (node.data) {
          allData.push({ ...node.data, no_seq_sort: index + 1 });
        }
      });

      setRowData(allData);
    }
  };
  //#endregion

  const handleInternalSelectionChanged = (
    gridRef: any,
    onSelectionChanged?: (selectedRows: any[]) => void
  ) => {
    if (gridRef?.current?.api && onSelectionChanged) {
      const selectedRows = gridRef.current.api.getSelectedRows(); //  선택된 행 가져오기
      onSelectionChanged(selectedRows); //  부모 컴포넌트로 전달
    }
  };

  /**
   * 코드박스 key in 시, setValue 공통처리 메서드
   */
  const handleCodeBoxValidation = async (
    node: IRowNode | null | undefined,
    colDef: any,
    newValue: string
  ) => {
    if (!node) return;

    const cd_popup = colDef.cd_popup;
    const cd_clas = node.data?.cd_clas ?? "";
    const field = colDef.field as string | undefined;
    if (!field || !newValue) return;

    try {
      const popupSetting = PopupSettings[cd_popup];
      if (!popupSetting) return;

      // code 일치 조회
      const param1 = {
        id: cd_popup.toLowerCase() + "_02",
        params: {
          cd_firm: userInfo.cd_firm,
          [popupSetting.str_search]: newValue,
          cd_clas,
        },
      };
      const result1 = await fnGetDataSet(param1);
      if (result1[0]?.length === 1) {
        const found = result1[0][0];
        const columns = popupSetting.columns || [];
        columns.forEach((colName: string, index: number) => {
          const gridApi = gridRef.current?.api;
          if (!gridApi) return;
          const colDef = gridApi.getColumnDef(colName);
          if (!colDef) return;

          const value = found[colName];
          if (value !== undefined) {
            node.setDataValue(colName, value);
          }
        });
        return;
      }

      // code, name like 조회
      const param2 = {
        id: cd_popup.toLowerCase() + "_03",
        params: {
          cd_firm: userInfo.cd_firm,
          str_search: newValue,
          cd_clas,
        },
      };
      const result2 = await fnGetDataSet(param2);

      if (result2[0]?.length === 1) {
        const found = result2[0][0];
        const columns = popupSetting.columns || [];
        columns.forEach((colName: string, index: number) => {
          const gridApi = gridRef.current?.api;
          if (!gridApi) return;
          const colDef = gridApi.getColumnDef(colName);
          if (!colDef) return;

          const value = found[colName];
          if (value !== undefined) {
            node.setDataValue(colName, value);
          }
        });
        return;
      }

      // 검색 결과 없거나 2건 이상 → 팝업 오픈
      const singleSetting = singlepopupsRef.current.filter(
        (popup: any) => popup.cd_column === field
      );
      const multiSetting = multipopupsRef.current.filter(
        (popup: any) => popup.cd_column === field
      );
      const popupSettingAll = getPopupSetting(
        field,
        singlepopupsRef.current,
        multipopupsRef.current
      );

      if (popupSettingAll.all.length > 0 && !isProcessingRef.current) {
        isProcessingRef.current = true;
        await popupHelper(
          node,
          singleSetting,
          multiSetting,
          popupSettingAll,
          field,
          memoizedColumnConfig,
          addRow,
          gridRef,
          popupCons
        );
        isProcessingRef.current = false;
      }
    } catch (e) {
      console.error("SnCodeBox validation error:", e);
    }
  };

  const handleProcessCellFromClipboard = async (params: any) => {
    const { column, node, value, api } = params;
    const colDef = column.getColDef();
    if ((colDef as any).tp_column === "SnCodeBox" && value) {
      await handleCodeBoxValidation(node, colDef, value);
      api?.tabToNextCell();
      return node?.data?.[colDef.field as string] ?? value;
    }
    return value;
  };

  const [statusBar] = useState({
    statusPanels: [
      {
        statusPanel: "agAggregationComponent",
        statusPanelParams: {
          aggFuncs: ["sum", "count"],
        },
        key: "aggregation",
        align: "left",
      },
    ],
  });
  // 테스트용입니다
  const [showFooter, setShowFooter] = useState(true);
  useEffect(() => {
    if (props.menuInfo?.cd_menu === "SN_SAL_ALS_REP_001") {
      setShowFooter(false);
    }
  }, [props.menuInfo?.cd_menu]);

  const renderGridContent = () => {
    if (!columnConfig || columnConfig.length === 0) {
      return;
    }
    return (
      <div
        className="snGrid"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
      >
        {/* 그리드 */}
        <div className="ag-theme-alpine" style={{ flex: "1 1 auto", ...style }}>
          <SnFilterSearchBox
            gridRef={gridRef}
            ref={filterBoxRef}
            columnConfig={columnConfig}
          />
          <AgGridReact
            rowStyle={{ fontSize: StyleSettings.font }}
            containerStyle={{ fontSize: StyleSettings.font }}
            gridOptions={gridOptionsExt}
            ref={gridRef}
            columnDefs={columnDefsWithRowNumber}
            rowData={rowData}
            // pinnedBottomRowData={pinnedBottomRowData}
            pinnedBottomRowData={showFooter ? pinnedBottomRowData : undefined}
            defaultColDef={defaultColDef}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            enableRangeSelection={true}
            animateRows={animateRows}
            rowDragManaged={rowDragManaged}
            cellSelection={cellSelection}
            suppressNoRowsOverlay={suppressNoRowsOverlay}
            suppressMenuHide={suppressMenuHide}
            rowGroupPanelShow={rowGroupPanelShow}
            enableCharts={enableExternalChart}
            localeText={localeText}
            onGridReady={handleGridReady}
            rowSelection={rowSelection}
            onRowClicked={handleInternalonRowClicked}
            // onCellClicked={onCellClicked}
            onRowDoubleClicked={handleOnRowDoubleClicked}
            // onCellDoubleClicked={handleonCellDoubleClick}
            onCellDoubleClicked={handleonCellDoubleClicked}
            onCellValueChanged={handleInternalCellValueChanged}
            domLayout="normal"
            suppressHorizontalScroll={false}
            suppressRowVirtualisation={false}
            onCellFocused={handleCellFocused} //포커스 변경 후 데이터 처리
            onCellKeyDown={handleCellKeyDown} // 키보드 입력 처리
            popupParent={popupParent || document.querySelector("body")} // 우클릭 크기 그리드 고정 제외
            suppressRowClickSelection={true}
            suppressFocusAfterRefresh={true}
            suppressCellFocus={false}
            // suppressRowTransform={true} //CSS Transform 애니메이션 비활성화
            onColumnMoved={handleColumnStateChange}
            onColumnResized={handleColumnStateChange}
            onColumnPinned={handleColumnStateChange}
            //onColumnVisible={handleColumnStateChange}
            onSelectionChanged={() =>
              handleInternalSelectionChanged(ref, onSelectionChanged)
            } //  추가
            headerHeight={30} // header 높이 설정
            getRowHeight={() => {
              return 30;
            }} // cell, footer 높이 설정
            // stopEditingWhenCellsLoseFocus={true} // grid 외부 클릭 시 editing 비활성화
            {...gridOptions}
            onRowDragEnd={handleRowDragEnd} // 드래그 종료 후
            autoGroupColumnDef={autoGroupColumnDef} // 그룹화했을 때 컬럼
            groupDefaultExpanded={-1} // 그룹화할 때 전체확장: -1, 전체축소: 0
            onCellContextMenu={handleContextMenu} // 셀 우클릭할 때 파라미터 전송
            // statusBar={statusBar} // 드래그 했을 때 sum 나오는거 일단 추가
            suppressCellFlash={true} // 데이터 변경 시 자연스러움 추가
            suppressHeaderKeyboardEvent={true}
            context={{ keyMapRef, comboCache: comboCacheRef.current }}
            processCellFromClipboard={handleProcessCellFromClipboard}
          />
        </div>

        {/* 독립 차트 */}
        {enableExternalChart && (
          <div
            ref={chartContainerRef}
            style={{
              marginTop: "20px",
              height: "300px",
              width: "100%",
              border: "1px solid #ccc",
            }}
          >
            {/* 독립 차트가 렌더링됩니다 */}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {title ? (
        <SnTab
          titles={[title]}
          width={"100%"}
          header={[
            <SnTabHeader
              onBtnAddClick={onBtnAddClick}
              onBtnDeleteClick={onBtnDeleteClick}
              body={header}
            />,
          ]}
          body={[renderGridContent()]}
        />
      ) : (
        renderGridContent()
      )}
    </>
  );
});
export default React.memo(SnGrid);
